import { useEffect, useMemo, useState } from 'react';

import { GetUsersRequestDTO, MODULE, PdfScope } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { RowSelectionState } from '@tanstack/react-table';
import { useDebounce } from '@uidotdev/usehooks';
import { Link } from 'react-router-dom';

import manholeShrugVector from '@/assets/vectors/manhole-shrug.svg';
import SearchBar from '@/components/common/search-bar';
import DeleteIcon from '@/components/icons/delete';
import DownloadCsvIcon from '@/components/icons/download-csv';
import QuestionIcon from '@/components/icons/question';
import { DownloadPdfMenu } from '@/components/table-page/download-pdf-menu';
import { TablePageTable } from '@/components/table-page/table-page-table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { InviteUsers } from '@/components/users/invite-users';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useInfiniteScrollTrigger } from '@/hooks/use-infinite-scroll-trigger';
import { usePageConfig } from '@/hooks/use-page-config';
import { usePermissions } from '@/hooks/use-permissions';
import { useResetRowSelectionOnChange } from '@/hooks/use-reset-row-selection-on-change';
import { User } from '@/legacy/models/user';
import { getUsersInfiniteQueryOptions, getUsersStatsQueryOptions, userKeys } from '@/queries/users';
import { ApiService } from '@/services/api-v2';
import { useModulesStore } from '@/stores/modules.store';
import { useUserStore } from '@/stores/user.store';
import { openExportTab } from '@/utils/export-tab';
import { buildCsvExportFilename } from '@/utils/export-to-csv';
import { activeExportColumnIds, buildPdfExportJob, RegisterExportQuery } from '@/utils/register-pdf-export';

import { createGearbeltContributorsTableColumns } from './table-meta';

function ContributorsPage() {
  const { syncModuleColumns } = useModulesStore();
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const toast = useToast();
  const queryClient = useQueryClient();
  const currentUser = useUserStore((state) => state.user);
  const { canManageUsers, canExportCsv } = usePermissions();

  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebounce(searchTerm, 500);

  const {
    config: { columnOrder, sorting },
    loading: isLoadingPageConfig,
    updatePageConfig,
  } = usePageConfig(MODULE.GearbeltContributors, {
    columns: createGearbeltContributorsTableColumns(),
  });

  const {
    data: users,
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetching: isFetchingUsers,
    isFetchingNextPage: isFetchingNextUsersPage,
    error: usersError,
  } = useInfiniteQuery(
    getUsersInfiniteQueryOptions({
      search: debouncedSearch,
      orderBy: sorting.key ? { field: sorting.key as NonNullable<GetUsersRequestDTO['orderBy']>['field'], direction: sorting.direction ?? 'asc' } : undefined,
      enabled: !isLoadingPageConfig,
    }),
  );

  const getSelectedUsers = useMemo(() => {
    return (incomingData: typeof users) => {
      if (!incomingData?.pages) return [];
      const ids = Object.keys(selectedRows);
      const users = incomingData.pages.flatMap((page) => page.data);
      return ids.filter((id) => !isNaN(parseInt(id))).map((id) => users[parseInt(id)]);
    };
  }, [selectedRows]);

  const selectedUsers = useMemo(() => getSelectedUsers(users), [users, getSelectedUsers]);

  const initialSorting = useMemo(
    () =>
      sorting.key
        ? [
            {
              id: sorting.key,
              desc: sorting.direction === 'desc',
            },
          ]
        : [],
    [sorting.key, sorting.direction],
  );

  useEffect(() => {
    syncModuleColumns(MODULE.GearbeltContributors, createGearbeltContributorsTableColumns());
  }, [syncModuleColumns]);

  useEffect(() => {
    if (usersError) {
      toast.error(usersError.message);
    }
  }, [toast, usersError]);

  useResetRowSelectionOnChange(setSelectedRows, [debouncedSearch, sorting.key, sorting.direction]);

  const { sentinelRef, canLoadMore, loadMore } = useInfiniteScrollTrigger({
    fetchNextPage: fetchNextUsersPage,
    hasNextPage: hasNextUsersPage,
    isFetching: isFetchingUsers,
    isError: !!usersError,
  });

  const flattenedData = useMemo(() => users?.pages.flatMap((page) => page.data) ?? [], [users]);

  const handleDelete = () => {
    ApiService.DeleteUsers({ userIds: selectedUsers.map((user) => user.id) })
      .then(() => {
        toast.success('Contributors removed successfully');
        setShowRemoveConfirmation(false);
        setSelectedRows({});
        queryClient.invalidateQueries({ queryKey: userKeys.all });
      })
      .catch((e: Error) => toast.error(e.message));
  };

  const handleReinvite = (user: User) => {
    ApiService.ReinviteUser({ userId: user.id })
      .then(() => {
        toast.success('Invite sent successfully');
        queryClient.invalidateQueries({ queryKey: userKeys.all });
      })
      .catch((e: Error) => toast.error(e.message));
  };

  const isRegisterPdfExportEnabled = useFeatureFlag('enableRegisterPdfExport');

  function buildContributorsExportRequest(): RegisterExportQuery<'users'> {
    const selected = getSelectedUsers(users);
    return {
      queryType: 'users',
      filters: { id: selected.map((user) => user.id) },
      orderBy: sorting.key ? { field: sorting.key as NonNullable<GetUsersRequestDTO['orderBy']>['field'], direction: sorting.direction ?? 'asc' } : undefined,
      columns: activeExportColumnIds(columnOrder),
    };
  }

  function handleCsvExport() {
    if (!users?.pages) return;

    openExportTab({ kind: 'csv', request: { ...buildContributorsExportRequest(), filename: buildCsvExportFilename('users') } });
  }

  function handlePdfScopeSelect(scope: PdfScope) {
    if (!users?.pages) return;
    openExportTab(buildPdfExportJob({ scope, records: [], register: buildContributorsExportRequest() }));
  }

  const { data: usersStatsData } = useQuery(getUsersStatsQueryOptions());
  const usersStats = usersStatsData?.usersStats;

  return (
    <div className="bg-grey-1 flex flex-1 flex-col px-6">
      <div className="mx-auto flex h-full w-full flex-1 flex-col gap-4 py-6">
        <div className="flex h-full flex-col gap-4 rounded-md bg-white px-4 py-4 shadow-lg">
          <div className="flex items-center justify-between">
            <h3 className="text-xl font-medium">
              Contributors
              {usersStats?.contributors?.active ? (
                <TooltipProvider>
                  <Tooltip delayDuration={0}>
                    <TooltipTrigger type="button">
                      <span className="text-grey-5 ml-1 text-base">({usersStats?.contributors?.active})</span>
                    </TooltipTrigger>
                    <TooltipPortal>
                      <TooltipContent side="bottom" sideOffset={0} className="bg-default-text max-w-[200px] border-none p-1 text-xs text-white">
                        <TooltipArrow className="fill-default-text" />
                        {usersStats?.contributors?.active} active users
                      </TooltipContent>
                    </TooltipPortal>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                ``
              )}
            </h3>
            <Link
              to="https://help.sitemate.com/en/articles/11476123-what-user-permissions-are-available-for-gearbelt-contributors"
              target="_blank"
              className="bg-grey-1 inline-flex w-auto gap-1 rounded p-1 text-sm font-medium">
              <QuestionIcon className="h-4 w-4" />
              Learn more
            </Link>
          </div>
          <div className="flex flex-col gap-2">
            <div className="text-sm">These users have access to this Gearbelt workspace.</div>
            {canManageUsers && <InviteUsers />}
          </div>
          <div>
            <div className="border-grey-3 flex flex-1 flex-col rounded-t border-x border-y">
              <div className="bg-grey-2 flex flex-shrink-0 items-center gap-2 overflow-x-hidden rounded-t px-2 py-1">
                {selectedUsers.length === 0 && <SearchBar className="w-[300px]" search={searchTerm} setSearch={setSearchTerm} />}
                {selectedUsers.length > 0 && (
                  <>
                    {canExportCsv && (
                      <Button variant="ghost" icon={<DownloadCsvIcon />} className="bg-grey-1 hover:bg-grey-3 px-2" onClick={handleCsvExport}>
                        Export CSV
                      </Button>
                    )}
                    {canExportCsv && isRegisterPdfExportEnabled && <DownloadPdfMenu variant="inline" onSelect={handlePdfScopeSelect} />}
                    {canManageUsers && (
                      <Button
                        variant="ghost"
                        icon={<DeleteIcon className="text-default-red" />}
                        className="bg-grey-1 hover:bg-grey-3 px-2"
                        onClick={() => setShowRemoveConfirmation(true)}>
                        Remove
                      </Button>
                    )}
                  </>
                )}
              </div>
              <div className="relative overflow-x-auto overflow-y-hidden border-t">
                <TablePageTable
                  columns={createGearbeltContributorsTableColumns({
                    onReinvite: handleReinvite,
                    currentUserRoles: currentUser?.roles,
                    currentUserId: currentUser?.id,
                  })}
                  data={flattenedData || []}
                  loading={(isFetchingUsers && !isFetchingNextUsersPage) || isLoadingPageConfig}
                  tableClassName="table-fixed [&_tr:last-child_td]:border-b-0 min-w-[1024px]"
                  cellClassName="border-x-transparent"
                  headClassName="!border-none"
                  headerClassName="after:!hidden before:!hidden"
                  sortButtonClassName="justify-start"
                  onRowSelectionChange={setSelectedRows}
                  rowSelection={selectedRows}
                  initialSorting={initialSorting}
                  onSortingChange={(sorting) =>
                    updatePageConfig({
                      table: { sorting: sorting },
                    })
                  }
                  globalFilter={searchTerm}
                  notFoundDisplay={
                    isFetchingUsers || debouncedSearch !== '' ? (
                      <NoContributorsFoundFromSearchDisplay />
                    ) : !isFetchingUsers && debouncedSearch === '' ? (
                      <NoContributorsAvailableDisplay />
                    ) : (
                      <></>
                    )
                  }
                />
              </div>
            </div>
            {hasNextUsersPage && (
              <div className="border-grey-3 w-full border">
                <Button ref={sentinelRef} onClick={loadMore} disabled={!canLoadMore} className="mx-auto w-full text-center" variant="ghost">
                  {isFetchingNextUsersPage ? 'Loading more...' : 'Load Newer'}
                </Button>
              </div>
            )}
          </div>
        </div>

        {showRemoveConfirmation && (
          <Dialog open={showRemoveConfirmation} onOpenChange={(open) => !open && setShowRemoveConfirmation(false)}>
            <DialogContent className="flex h-fit min-w-[500px] flex-col gap-4">
              <DialogHeader>
                <DialogTitle>&nbsp;</DialogTitle>
              </DialogHeader>
              <div className="flex w-full justify-center text-xl font-medium">Remove selected users?</div>
              <div className="flex w-full justify-center text-sm font-normal">Are you sure you want to remove access for the selected users?</div>
              <div className="flex w-full justify-center gap-2">
                <Button type="button" size="sm" variant="destructive" onClick={() => handleDelete()} className="gap-1">
                  <DeleteIcon /> Remove
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowRemoveConfirmation(false)}>
                  Cancel
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

function NoContributorsFoundFromSearchDisplay() {
  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white px-4 py-16">
      <img src={manholeShrugVector} alt="User" className="h-28" />
      <span className="text-xl leading-[30px] font-medium">Oops! No results</span>
    </div>
  );
}

function NoContributorsAvailableDisplay() {
  return (
    <div className="items-left flex w-full flex-col gap-1 bg-white p-2">
      <div className="text-secondary-text text-sm font-normal">No Contributors have been added to this Gearbelt workspace yet</div>
    </div>
  );
}

export default ContributorsPage;
